/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Container } from '@/components/container';
import { textFieldValidation } from '@/utils/validations';
import { normalizeNationalPhoneNumber } from '@/utils/phone';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { KeenIcon } from '@/components';
import PriceInput from '@/components/inputControls/PriceInput';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import {
  IServiceData,
  ServiceLanguage,
  ServiceType,
  SubCategoryItem,
  IServiceTranslationContent,
  BookingSlotType,
  HarbourServiceModel,
} from '@/types/service-management.types';
import PreviewModal from './components/PreviewModal';
import {
  useLazyGetSubCategoriesQuery,
  useTranslateServiceMutation,
  useGetServiceByIdQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
} from '@/services/ServiceManagementService';
import {
  useCreateHarborServiceMutation,
  useUpdateHarborServiceMutation,
} from '@/services/HarborServiceManagementService';
import { useApproveAndCreateServiceMutation } from '@/services/ServiceRequestManagement';
import { useGetServiceProvidersQuery } from '@/services/ServiceProviderService';
import { PaginatedApiResponse } from '@/types/api.types';
import { ServiceProviderResponse } from '@/types/service-provider.types';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { UserRoles } from '@/constant';
import { ErrorApiResponse } from '@/types/api.types';
import MediaUpload from '../habor-management/MediaUpload';
import { I18N_LANGUAGES } from '@/i18n';
import NumberInput from '@/components/inputControls/NumberInput';
import { useManagedListOptions } from '@/hooks/useManagedListOptions';
import { ManagedListType } from '@/types/list-value.types';
import CountryCodeSelect from '@/components/CountryCodeSelect/CountryCodeSelect';

const KEY_BOX_CONTENT_LANGS = ['en', 'fi', 'sv', 'de'] as const;

const SOURCE_LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  fi: 'Finnish',
  sv: 'Swedish',
  de: 'German',
};

interface ServiceFormPageProps {
  mode: 'create' | 'edit' | 'view';
}

// Utility function to format label text
const formatLabel = (text: string): string => {
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const baseInputClassName =
  'input input-bordered w-full h-11 rounded-lg border-gray-300 text-gray-900 disabled:text-gray-700 disabled:opacity-100';

const ServiceFormPage = ({ mode }: ServiceFormPageProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const {
    options: serviceCategoryOptions,
    isLoading: isLoadingServiceCategories,
  } = useManagedListOptions(ManagedListType.SERVICE_CATEGORY);

  const serviceId = mode === 'edit' || mode === 'view' ? params.id : undefined;
  const serviceProviderId = searchParams.get('serviceProviderId');
  const hideServiceProvider =
    searchParams.get('hideServiceProvider') === 'true';
  const showDistanceFromHarbor =
    searchParams.get('showDistanceFromHarbor') === 'true';

  const serviceRequestName = searchParams.get('name');
  const serviceRequestDescription = searchParams.get('description');
  const serviceRequestCategory = searchParams.get('category');
  const serviceRequestId = searchParams.get('serviceRequestId');

  // Get harborId from URL params or location state
  const harborId =
    searchParams.get('harborId') ||
    (location.state as { harborId?: string })?.harborId;

  const nameInputRef = useRef<HTMLInputElement>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [showPreviewButton, setShowPreviewButton] = useState(false);
  const [isTranslatingPreview, setIsTranslatingPreview] = useState(false);
  const [subCategories, setSubCategories] = useState<
    (SubCategoryItem & { value: string; label: string })[]
  >([]);
  const [fetchSubCategories, { isLoading: isLoadingSubCategories }] =
    useLazyGetSubCategoriesQuery();
  const userData = useSelector((state: RootState) => state.auth.userData);
  const isAdmin = userData?.roles?.includes(UserRoles.ADMIN);

  const [formData, setFormData] = useState<Partial<IServiceData>>({
    name: '',
    description: '',
    category: '',
    sub_category: '',
    service_type: '',
    language: 'en',
    source_language: 'en',
    translations: {},
    media: [],
    media_captions: [],
    linked_harbor_ids: [],
    service_provider_id: serviceProviderId || '',
    session_slots: [],
    access_handover_mode: 'NONE',
    transaction_presentation_mode: null,
    key_box_code: '',
    key_box_pickup_instructions: '',
    key_box_return_instructions: '',
    harbour_service_model: undefined,
    contact_email: '',
    contact_phone: '',
    country_code: '+358',
    country_short_code: 'FI',
    contact_phone_country_short_code: 'FI',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createService] = useCreateServiceMutation();
  const [updateService] = useUpdateServiceMutation();
  const [createHarborService] = useCreateHarborServiceMutation();
  const [updateHarborService] = useUpdateHarborServiceMutation();
  const [translateService] = useTranslateServiceMutation();
  const [approveAndCreateService] = useApproveAndCreateServiceMutation();

  const { data: serviceProvidersData, isLoading: isLoadingProviders } =
    useGetServiceProvidersQuery(
      { status: 1 },
      { skip: !!harborId }
    ) as unknown as {
      data: PaginatedApiResponse<ServiceProviderResponse[]> | undefined;
      isLoading: boolean;
    };

  const {
    data: serviceResponse,
    isLoading: isServiceLoading,
    isError: isServiceError,
  } = useGetServiceByIdQuery(serviceId!, {
    skip: !serviceId || mode === 'create',
  });

  // Load service data for edit/view mode
  useEffect(() => {
    if (serviceResponse?.data && (mode === 'edit' || mode === 'view')) {
      const serviceData = serviceResponse.data;
      const linkedHarborIds = serviceData.linked_harbors?.length
        ? Array.from(new Set(serviceData.linked_harbors.map((h) => h.id)))
        : [];
      const sourceLanguage =
        serviceData.language || serviceData.source_language || 'en';
      const mediaArray =
        serviceData.images && serviceData.images.length > 0
          ? serviceData.images
          : [];

      const updatedFormData = {
        ...serviceData,
        source_language: sourceLanguage,
        linked_harbor_ids: linkedHarborIds,
        media: mediaArray,
        service_provider_id:
          (serviceData.service_provider as { id?: string } | undefined)?.id ||
          '',
        session_slots: serviceData.session_slots || [],
        media_captions:
          serviceData.image_captions && serviceData.image_captions.length > 0
            ? serviceData.image_captions.map((caption) => ({
                image_index: caption.image_index,
                captions: caption.translations,
              }))
            : [],
        contact_email: serviceData.contact_email || '',
        contact_phone: serviceData.contact_phone
          ? normalizeNationalPhoneNumber(
              serviceData.contact_phone,
              serviceData.contact_phone_country_short_code || serviceData.country_short_code
            )
          : '',
        country_code: serviceData.country_code || '',
        country_short_code: serviceData.country_short_code || 'FI',
        contact_phone_country_short_code: serviceData.contact_phone_country_short_code || serviceData.country_short_code || 'FI',
      };

      setFormData(updatedFormData);
      setErrors({});
    }
  }, [serviceResponse, mode]);

  // Fetch sub-categories when category changes
  useEffect(() => {
    if (formData.category) {
      const sourceLanguage = (formData.source_language ||
        ServiceLanguage.EN) as ServiceLanguage;

      if (
        subCategories.length === 0 ||
        !subCategories.some((sc) => sc.category === formData.category)
      ) {
        fetchSubCategories({
          category: formData.category,
          language: sourceLanguage,
        })
          .unwrap()
          .then((response) => {
            if (response.data) {
              const mappedSubCategories = response.data.map((item) => ({
                ...item,
                value: item.id,
                label: item.translations[sourceLanguage]?.name || item.name,
              }));
              setSubCategories(mappedSubCategories);

              if (formData.sub_category) {
                const matchingSubCat = mappedSubCategories.find(
                  (cat) =>
                    cat.name === formData.sub_category ||
                    cat.id === formData.sub_category
                );
                if (
                  matchingSubCat &&
                  matchingSubCat.id !== formData.sub_category
                ) {
                  setFormData((prev) => ({
                    ...prev,
                    sub_category: matchingSubCat.id,
                  }));
                }
              }
            }
          })
          .catch((error) => {
            console.error('Error fetching sub-categories:', error);
            if (subCategories.length > 0) {
              setSubCategories([]);
            }
          });
      }
    } else {
      if (subCategories.length > 0) {
        setSubCategories([]);
      }
      if (!formData.category && formData.sub_category !== '') {
        setFormData((prev) => ({ ...prev, sub_category: '' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.category,
    formData.source_language,
    fetchSubCategories,
    formData.sub_category,
  ]);

  // Set serviceProviderId from URL param
  useEffect(() => {
    if (serviceProviderId && mode === 'create') {
      setFormData((prev) => ({
        ...prev,
        service_provider_id: serviceProviderId,
      }));
    }
  }, [serviceProviderId, mode]);

  // Set harborId from URL param or state, default harbour_service_model, and sync independent_booking_enabled
  useEffect(() => {
    if (harborId && mode === 'create') {
      setFormData((prev) => {
        const model = prev.harbour_service_model ?? HarbourServiceModel.LISTING;
        return {
          ...prev,
          harbor_id: harborId,
          harbour_service_model: model,
          independent_booking_enabled: model === HarbourServiceModel.BOOKABLE,
        };
      });
    }
  }, [harborId, mode]);

  // Pre-fill basic fields from service request when creating from a request
  useEffect(() => {
    if (mode === 'create') {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || serviceRequestName || '',
        description: prev.description || serviceRequestDescription || '',
        category: prev.category || serviceRequestCategory || '',
      }));
    }
  }, [
    mode,
    serviceRequestName,
    serviceRequestDescription,
    serviceRequestCategory,
  ]);

  // Check if name and description are filled to show preview button
  useEffect(() => {
    const hasNameAndDescription =
      !!formData.name &&
      formData.name.trim().length >= 2 &&
      !!formData.description &&
      formData.description.trim().length >= 10;

    const noErrors = !errors.name && !errors.description;

    setShowPreviewButton(hasNameAndDescription && noErrors && mode !== 'view');
  }, [
    formData.name,
    formData.description,
    errors.name,
    errors.description,
    mode,
  ]);

  const validateField = useCallback(
    async (name: keyof IServiceData | string, value: unknown) => {
      try {
        const baseSchemaShape: Record<string, Yup.AnySchema> = {
          name: textFieldValidation('Service Name', 2, 100, true),
          description: textFieldValidation('Description', 10, 1000, true),
          category: Yup.string().required('Category is required'),
          sub_category: Yup.string().required('Sub Category is required'),
          service_type: Yup.string()
            .oneOf(Object.values(ServiceType), 'Invalid service type')
            .required('Service Type is required'),
          source_language: Yup.string()
            .oneOf(Object.values(ServiceLanguage), 'Invalid source language')
            .required('Source language is required'),
          service_provider_id:
            isAdmin && !hideServiceProvider
              ? Yup.string().required('Service Provider is required')
              : Yup.string().nullable(),
          independent_booking_enabled: Yup.boolean().nullable(),
          booking_slot_type: Yup.string()
            .oneOf(
              Object.values(BookingSlotType) as string[],
              'Invalid slot type'
            )
            .nullable()
            .when('independent_booking_enabled', {
              is: true,
              then: (schema) =>
                schema.required(
                  'Slot type is required when independent booking is enabled'
                ),
              otherwise: (schema) => schema.notRequired().nullable(),
            }),
          contact_email: Yup.string()
            .transform((value) => (value === '' ? null : value))
            .email('Must be a valid email address')
            .nullable()
            .notRequired(),
          contact_phone: Yup.string()
            .transform((value) => (value === '' ? null : value))
            .nullable()
            .notRequired()
            .test(
              'is-valid-phone',
              'Phone number is invalid for the selected country',
              function (value) {
                if (!value) return true;
                const countryShortCode = this.parent?.contact_phone_country_short_code || this.parent?.country_short_code;
                if (!countryShortCode) return true;
                try {
                  const phoneNumberObj = parsePhoneNumberFromString(
                    value,
                    countryShortCode as any
                  );
                  return phoneNumberObj?.isValid() ?? false;
                } catch {
                  return false;
                }
              }
            ),
          country_code: Yup.string()
            .transform((value) => (value === '' ? null : value))
            .nullable()
            .notRequired(),
          country_short_code: Yup.string()
            .transform((value) => (value === '' ? null : value))
            .nullable()
            .notRequired(),
          send_request_booking_to_admin: Yup.boolean().nullable(),
          quantity: Yup.number()
            .typeError('Capacity must be a valid number')
            .positive('Capacity must be greater than 0')
            .nullable()
            .when('independent_booking_enabled', {
              is: true,
              then: (schema) => schema.required('Capacity is required'),
              otherwise: (schema) => schema.notRequired().nullable(),
            }),
          session_slots: Yup.array()
            .of(
              Yup.object().shape({
                start_time: Yup.string().required('Start time is required'),
                end_time: Yup.string()
                  .required('End time is required')
                  .test(
                    'is-after-start',
                    'End time must be after start time',
                    function (value) {
                      const { start_time } = this.parent as {
                        start_time?: string;
                      };
                      if (!start_time || !value) return true;
                      return value > start_time;
                    }
                  ),
              })
            )
            .nullable()
            .when(['independent_booking_enabled', 'booking_slot_type'], {
              is: (enabled: boolean, slot: BookingSlotType) =>
                Boolean(enabled) && slot === BookingSlotType.SESSION,
              then: (schema) =>
                schema
                  .min(1, 'At least one session slot is required')
                  .test(
                    'no-overlap',
                    'Session slots cannot overlap',
                    function (slots) {
                      if (!slots || slots.length < 2) return true;
                      const sortedSlots = [...slots].sort((a, b) => {
                        if (!a.start_time || !b.start_time) return 0;
                        return a.start_time.localeCompare(b.start_time);
                      });
                      for (let i = 0; i < sortedSlots.length - 1; i++) {
                        const current = sortedSlots[i];
                        const next = sortedSlots[i + 1];
                        if (
                          current.end_time &&
                          next.start_time &&
                          current.end_time > next.start_time
                        ) {
                          return false;
                        }
                      }
                      return true;
                    }
                  ),
              otherwise: (schema) => schema.notRequired().nullable(),
            }),
          start_time: Yup.string()
            .nullable()
            .when(['independent_booking_enabled', 'booking_slot_type'], {
              is: (enabled: boolean, slot: BookingSlotType) =>
                Boolean(enabled) && slot === BookingSlotType.HOURLY,
              then: (schema) =>
                schema
                  .required('Start time is required for hourly slot type')
                  .matches(/^\d{2}:\d{2}$/u, 'Invalid time format (HH:mm)'),
              otherwise: (schema) => schema.notRequired().nullable(),
            }),
          end_time: Yup.string()
            .nullable()
            .when(
              [
                'independent_booking_enabled',
                'booking_slot_type',
                'start_time',
              ],
              {
                is: (enabled: boolean, slot: BookingSlotType) =>
                  Boolean(enabled) && slot === BookingSlotType.HOURLY,
                then: (schema) =>
                  schema
                    .required('End time is required for hourly slot type')
                    .matches(/^\d{2}:\d{2}$/u, 'Invalid time format (HH:mm)')
                    .test(
                      'is-after-start',
                      'End time must be after start time',
                      function (value) {
                        const { start_time } = this.parent as {
                          start_time?: string;
                        };
                        if (!start_time || !value) return true;
                        return value > start_time;
                      }
                    ),
                otherwise: (schema) => schema.notRequired().nullable(),
              }
            ),
          price_amount: Yup.number()
            .typeError('Price must be a valid number')
            .positive('Price must be greater than 0')
            .nullable()
            .notRequired(),
          transaction_presentation_mode: Yup.string().nullable().notRequired(),
          access_handover_mode: Yup.string()
            .oneOf(['NONE', 'KEY_BOX', 'ACCESS_CODE'])
            .notRequired(),
          key_box_code: Yup.string().when('access_handover_mode', {
            is: (m: string) => m === 'KEY_BOX' || m === 'ACCESS_CODE',
            then: (schema) =>
              schema.required('Key box code is required').trim(),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          key_box_pickup_instructions: Yup.string().when(
            'access_handover_mode',
            {
              is: (m: string) => m === 'KEY_BOX' || m === 'ACCESS_CODE',
              then: (schema) =>
                schema
                  .required('Pickup / usage instructions are required')
                  .trim(),
              otherwise: (schema) => schema.notRequired().nullable(),
            }
          ),
          key_box_return_instructions: Yup.string().when(
            'access_handover_mode',
            {
              is: 'KEY_BOX',
              then: (schema) =>
                schema.required('Return instructions are required').trim(),
              otherwise: (schema) => schema.notRequired().nullable(),
            }
          ),
          ...(harborId
            ? {
                harbour_service_model: Yup.string()
                  .oneOf(
                    Object.values(HarbourServiceModel),
                    'Invalid service model'
                  )
                  .required('Harbour service type is required'),
              }
            : {}),
          latitude: Yup.number()
            .transform((value, originalValue) => {
              if (
                originalValue === '' ||
                originalValue === null ||
                originalValue === undefined
              ) {
                return undefined;
              }
              if (typeof originalValue === 'string') {
                const normalized = originalValue
                  .replace(/\s/g, '')
                  .replace(',', '.');
                const parsed = Number(normalized);
                return Number.isNaN(parsed) ? NaN : parsed;
              }
              return value;
            })
            .typeError('Latitude must be a valid number')
            .min(-90, 'Latitude must be between -90 and 90')
            .max(90, 'Latitude must be between -90 and 90')
            .required('Latitude is required'),
          longitude: Yup.number()
            .transform((value, originalValue) => {
              if (
                originalValue === '' ||
                originalValue === null ||
                originalValue === undefined
              ) {
                return undefined;
              }
              if (typeof originalValue === 'string') {
                const normalized = originalValue
                  .replace(/\s/g, '')
                  .replace(',', '.');
                const parsed = Number(normalized);
                return Number.isNaN(parsed) ? NaN : parsed;
              }
              return value;
            })
            .typeError('Longitude must be a valid number')
            .min(-180, 'Longitude must be between -180 and 180')
            .max(180, 'Longitude must be between -180 and 180')
            .required('Longitude is required'),
        };

        const distanceSchemaShape = showDistanceFromHarbor
          ? {
              distance: Yup.number()
                .typeError('Distance must be a valid number')
                .positive('Distance must be greater than 0')
                .nullable()
                .notRequired(),
              distance_unit: Yup.string()
                .oneOf(['m', 'km'], 'Invalid distance unit')
                .nullable()
                .when('distance', {
                  is: (val: unknown) =>
                    val !== undefined && val !== null && val !== '',
                  then: (schema) =>
                    schema.required(
                      'Distance unit is required when distance is provided'
                    ),
                  otherwise: (schema) => schema.notRequired(),
                }),
            }
          : {};

        const schema = Yup.object().shape({
          ...baseSchemaShape,
          ...(distanceSchemaShape as Record<string, Yup.AnySchema>),
        });

        await schema.validateAt(
          name as string,
          {
            ...formData,
            [name]: name === 'source_language' ? value || 'en' : value,
          },
          { abortEarly: true }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => {
            const newError = err.errors[0] || 'Invalid input';
            if (prev[name] === newError) return prev;
            return { ...prev, [name]: newError };
          });
        }
      }
    },
    [
      setErrors,
      formData,
      isAdmin,
      hideServiceProvider,
      showDistanceFromHarbor,
      harborId,
    ]
  );

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name } = e.target;
      const isCheckbox =
        (e.target as HTMLInputElement).type === 'checkbox' &&
        (e.target as HTMLInputElement).checked !== undefined;
      const value = isCheckbox
        ? (e.target as HTMLInputElement).checked
        : (
            e.target as
              | HTMLInputElement
              | HTMLTextAreaElement
              | HTMLSelectElement
          ).value;

      if (name === 'distance') {
        const numericValue =
          value === '' ? undefined : Number(value.toString().replace(',', '.'));

        setFormData((prev: Partial<IServiceData>) => ({
          ...prev,
          distance: Number.isNaN(numericValue) ? undefined : numericValue,
        }));

        validateField('distance', numericValue);
        return;
      }

      if (name === 'quantity') {
        const numericValue = value === '' ? undefined : Number(value);
        setFormData((prev: Partial<IServiceData>) => ({
          ...prev,
          quantity: Number.isNaN(numericValue)
            ? undefined
            : (numericValue as number | undefined),
        }));
        validateField('quantity', numericValue);
        return;
      }

      if (name === 'distance_unit') {
        setFormData((prev: Partial<IServiceData>) => ({
          ...prev,
          distance_unit: value === '' ? undefined : (value as 'm' | 'km'),
        }));

        validateField('distance_unit', value);
        return;
      }

      if (name === 'independent_booking_enabled') {
        const enabled = Boolean(value);
        setFormData((prev: Partial<IServiceData>) => ({
          ...prev,
          independent_booking_enabled: enabled,
          booking_slot_type: enabled ? prev.booking_slot_type : undefined,
          start_time: enabled ? prev.start_time : undefined,
          end_time: enabled ? prev.end_time : undefined,
        }));
        validateField('independent_booking_enabled', enabled);
        if (!enabled) {
          validateField('booking_slot_type', undefined);
          validateField('start_time', undefined);
          validateField('end_time', undefined);
          validateField('session_slots', []);
        }
        return;
      }

      if (name === 'booking_slot_type') {
        const slot = value as BookingSlotType;
        setFormData((prev: Partial<IServiceData>) => ({
          ...prev,
          booking_slot_type: slot,
          start_time:
            slot === BookingSlotType.HOURLY ? prev.start_time : undefined,
          end_time: slot === BookingSlotType.HOURLY ? prev.end_time : undefined,
        }));

        validateField('booking_slot_type', slot);
        if (slot !== BookingSlotType.HOURLY) {
          validateField('start_time', undefined);
          validateField('end_time', undefined);
        }
        if (slot !== BookingSlotType.SESSION) {
          validateField('session_slots', []);
        }
        return;
      }

      if (name === 'start_time' || name === 'end_time') {
        let processedValue = value as string;
        if (processedValue) {
          const timeParts = processedValue.split(':');
          if (timeParts.length >= 2) {
            processedValue = `${timeParts[0]}:00`;
          }
        }

        setFormData((prev: Partial<IServiceData>) => ({
          ...prev,
          [name]: processedValue,
        }));
        validateField(name, processedValue);
        return;
      }

      if (name === 'harbour_service_model') {
        const model = value as HarbourServiceModel;
        setFormData((prev) => ({
          ...prev,
          harbour_service_model: model,
          independent_booking_enabled: model === HarbourServiceModel.BOOKABLE,
          ...(model === HarbourServiceModel.LISTING
            ? {
                transaction_presentation_mode: null,
                access_handover_mode: 'NONE' as const,
                key_box_code: '',
                key_box_pickup_instructions: '',
                key_box_return_instructions: '',
                booking_slot_type: undefined,
                start_time: undefined,
                end_time: undefined,
                session_slots: [],
              }
            : {}),
        }));
        return;
      }

      if (name === 'access_handover_mode') {
        const v = value as string;
        if (v === 'NONE') {
          setFormData((prev) => {
            const tr = { ...(prev.translations || {}) };
            for (const lc of Object.keys(tr)) {
              const row = tr[lc] as IServiceTranslationContent;
              if (!row) continue;
              const next: IServiceTranslationContent = { ...row };
              delete (next as { key_box_pickup_instructions?: string })
                .key_box_pickup_instructions;
              delete (next as { key_box_return_instructions?: string })
                .key_box_return_instructions;
              tr[lc] = next;
            }
            return {
              ...prev,
              access_handover_mode: v as 'NONE' | 'KEY_BOX' | 'ACCESS_CODE',
              key_box_code: '',
              key_box_pickup_instructions: '',
              key_box_return_instructions: '',
              translations: tr,
            };
          });
          setErrors((prev) => ({
            ...prev,
            key_box_code: '',
            key_box_pickup_instructions: '',
            key_box_return_instructions: '',
          }));
        } else {
          setFormData((prev: Partial<IServiceData>) => ({
            ...prev,
            access_handover_mode: v as 'NONE' | 'KEY_BOX' | 'ACCESS_CODE',
          }));
        }
        validateField('access_handover_mode', v);
        return;
      }

      if (name === 'source_language') {
        const newLang = (value as string) || 'en';
        setFormData((prev) => {
          const oldLang = prev.source_language || 'en';
          if (oldLang === newLang) {
            return { ...prev, source_language: newLang };
          }
          const tr = {
            ...(prev.translations || {}),
          } as Record<string, IServiceTranslationContent>;
          if (
            prev.access_handover_mode === 'KEY_BOX' ||
            prev.access_handover_mode === 'ACCESS_CODE'
          ) {
            tr[oldLang] = {
              ...(tr[oldLang] || {}),
              name: tr[oldLang]?.name ?? prev.name ?? '',
              description: tr[oldLang]?.description ?? prev.description ?? '',
              key_box_pickup_instructions:
                (prev.key_box_pickup_instructions ?? '').trim() ||
                tr[oldLang]?.key_box_pickup_instructions,
              key_box_return_instructions:
                (prev.key_box_return_instructions ?? '').trim() ||
                tr[oldLang]?.key_box_return_instructions,
            };
            const nextRow = tr[newLang];
            return {
              ...prev,
              source_language: newLang,
              translations: tr,
              key_box_pickup_instructions:
                nextRow?.key_box_pickup_instructions ?? '',
              key_box_return_instructions:
                nextRow?.key_box_return_instructions ?? '',
            };
          }
          return { ...prev, source_language: newLang };
        });
        validateField('source_language', newLang);
        return;
      }

      const key = name as keyof IServiceData;

      setFormData((prev: Partial<IServiceData>) => ({
        ...prev,
        [key]: value,
      }));

      validateField(key, value);
    },
    [setFormData, validateField]
  );

  const handleInputBlur = useCallback(
    (name: string, value: unknown) => {
      validateField(name, value);
    },
    [validateField]
  );

  const handleAddSessionSlot = useCallback(() => {
    const newSlots = [
      ...(formData.session_slots || []),
      { start_time: '', end_time: '' },
    ];
    setFormData((prev) => ({
      ...prev,
      session_slots: newSlots,
    }));
    validateField('session_slots', newSlots);
  }, [formData.session_slots, validateField]);

  const handleRemoveSessionSlot = useCallback(
    (index: number) => {
      const newSlots = (formData.session_slots || []).filter(
        (_, i) => i !== index
      );
      setFormData((prev) => ({
        ...prev,
        session_slots: newSlots,
      }));
      validateField('session_slots', newSlots);
    },
    [formData.session_slots, validateField]
  );

  const handleSessionSlotChange = useCallback(
    (index: number, field: 'start_time' | 'end_time', value: string) => {
      const newSlots = [...(formData.session_slots || [])];
      newSlots[index] = { ...newSlots[index], [field]: value };
      setFormData((prev) => ({ ...prev, session_slots: newSlots }));
      validateField('session_slots', newSlots);
    },
    [formData.session_slots, validateField]
  );

  const handleOpenPreview = useCallback(async () => {
    if (!formData.name) return;

    // If translations already exist, open preview directly without calling the translate API
    if (
      formData.translations &&
      Object.keys(formData.translations).length > 0
    ) {
      setIsPreviewModalOpen(true);
      return;
    }

    try {
      setIsTranslatingPreview(true);
      const translateResponse = await translateService({
        description: formData.description || '',
        source_language: formData.source_language || 'en',
      }).unwrap();

      if (translateResponse.status && translateResponse.translations) {
        setFormData((prev) => ({
          ...prev,
          translations: translateResponse.translations,
        }));
      }
      setIsPreviewModalOpen(true);
    } catch (error) {
      console.error('Translation failed:', error);
      enqueueSnackbar('Failed to generate translations', {
        variant: 'warning',
      });
      // Still open preview modal so user can manually translate/view
      setIsPreviewModalOpen(true);
    } finally {
      setIsTranslatingPreview(false);
    }
  }, [
    formData.name,
    formData.description,
    formData.source_language,
    formData.translations,
    translateService,
    enqueueSnackbar,
  ]);

  const handleSourceFormDataChange = useCallback(
    (fields: { name?: string; description?: string }) => {
      setFormData((prev) => ({
        ...prev,
        ...fields,
      }));
    },
    []
  );

  const handleTranslationsUpdate = useCallback(
    (translations: Record<string, IServiceTranslationContent>) => {
      setFormData((prev) => ({
        ...prev,
        translations: Object.fromEntries(
          Object.entries(translations).map(([lang, content]) => [
            lang,
            {
              name: content.name || formData.name || '',
              description: content.description || '',
              key_box_pickup_instructions:
                content.key_box_pickup_instructions ??
                prev.translations?.[lang]?.key_box_pickup_instructions,
              key_box_return_instructions:
                content.key_box_return_instructions ??
                prev.translations?.[lang]?.key_box_return_instructions,
            },
          ])
        ),
      }));
    },
    [formData.name, setFormData]
  );

  const handleKeyBoxLangFieldChange = useCallback(
    (
      lang: string,
      field: 'key_box_pickup_instructions' | 'key_box_return_instructions',
      value: string
    ) => {
      setFormData((prev) => ({
        ...prev,
        translations: {
          ...(prev.translations || {}),
          [lang]: {
            ...(prev.translations?.[lang] || {}),
            name: prev.translations?.[lang]?.name ?? prev.name ?? '',
            description: prev.translations?.[lang]?.description ?? '',
            [field]: value,
          },
        },
      }));
    },
    []
  );

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const formDataWithDefaults = {
        ...formData,
        source_language: formData.source_language || 'en',
      };

      const baseSchemaShape: Record<string, Yup.AnySchema> = {
        name: textFieldValidation('Service Name', 2, 100, true),
        description: textFieldValidation('Description', 10, 1000, true),
        category: Yup.string().required('Category is required'),
        sub_category: Yup.string().required('Sub Category is required'),
        service_type: Yup.string()
          .oneOf(Object.values(ServiceType), 'Invalid service type')
          .required('Service Type is required'),
        source_language: Yup.string()
          .oneOf(Object.values(ServiceLanguage), 'Invalid source language')
          .required('Source language is required'),
        service_provider_id:
          isAdmin && !hideServiceProvider
            ? Yup.string().required('Service Provider is required')
            : Yup.string().nullable(),
        independent_booking_enabled: Yup.boolean().nullable(),
        booking_slot_type: Yup.string()
          .oneOf(
            Object.values(BookingSlotType) as string[],
            'Invalid slot type'
          )
          .nullable()
          .when('independent_booking_enabled', {
            is: true,
            then: (schema) =>
              schema.required(
                'Slot type is required when independent booking is enabled'
              ),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
        contact_email: Yup.string()
          .transform((value) => (value === '' ? null : value))
          .email('Must be a valid email address')
          .nullable()
          .notRequired(),
        contact_phone: Yup.string()
          .transform((value) => (value === '' ? null : value))
          .nullable()
          .notRequired()
          .test(
            'is-valid-phone',
            'Phone number is invalid for the selected country',
            function (value) {
              if (!value) return true;
              const countryShortCode = this.parent?.country_short_code;
              if (!countryShortCode) return true;
              try {
                const phoneNumberObj = parsePhoneNumberFromString(
                  value,
                  countryShortCode as any
                );
                return phoneNumberObj?.isValid() ?? false;
              } catch {
                return false;
              }
            }
          ),
        country_code: Yup.string()
          .transform((value) => (value === '' ? null : value))
          .nullable()
          .notRequired(),
        country_short_code: Yup.string()
          .transform((value) => (value === '' ? null : value))
          .nullable()
          .notRequired(),
        send_request_booking_to_admin: Yup.boolean().nullable(),
        quantity: Yup.number()
          .typeError('Capacity must be a valid number')
          .positive('Capacity must be greater than 0')
          .nullable()
          .when('independent_booking_enabled', {
            is: true,
            then: (schema) => schema.required('Capacity is required'),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
        session_slots: Yup.array()
          .of(
            Yup.object().shape({
              start_time: Yup.string().required('Start time is required'),
              end_time: Yup.string()
                .required('End time is required')
                .test(
                  'is-after-start',
                  'End time must be after start time',
                  function (value) {
                    const { start_time } = this.parent as {
                      start_time?: string;
                    };
                    if (!start_time || !value) return true;
                    return value > start_time;
                  }
                ),
            })
          )
          .nullable()
          .when(['independent_booking_enabled', 'booking_slot_type'], {
            is: (enabled: boolean, slot: BookingSlotType) =>
              Boolean(enabled) && slot === BookingSlotType.SESSION,
            then: (schema) =>
              schema
                .min(1, 'At least one session slot is required')
                .test(
                  'no-overlap',
                  'Session slots cannot overlap',
                  function (slots) {
                    if (!slots || slots.length < 2) return true;
                    const sortedSlots = [...slots].sort((a, b) => {
                      if (!a.start_time || !b.start_time) return 0;
                      return a.start_time.localeCompare(b.start_time);
                    });
                    for (let i = 0; i < sortedSlots.length - 1; i++) {
                      const current = sortedSlots[i];
                      const next = sortedSlots[i + 1];
                      if (
                        current.end_time &&
                        next.start_time &&
                        current.end_time > next.start_time
                      ) {
                        return false;
                      }
                    }
                    return true;
                  }
                ),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
        start_time: Yup.string()
          .nullable()
          .when(['independent_booking_enabled', 'booking_slot_type'], {
            is: (enabled: boolean, slot: BookingSlotType) =>
              Boolean(enabled) && slot === BookingSlotType.HOURLY,
            then: (schema) =>
              schema
                .required('Start time is required for hourly slot type')
                .matches(/^\d{2}:\d{2}$/u, 'Invalid time format (HH:mm)'),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
        end_time: Yup.string()
          .nullable()
          .when(
            ['independent_booking_enabled', 'booking_slot_type', 'start_time'],
            {
              is: (enabled: boolean, slot: BookingSlotType) =>
                Boolean(enabled) && slot === BookingSlotType.HOURLY,
              then: (schema) =>
                schema
                  .required('End time is required for hourly slot type')
                  .matches(/^\d{2}:\d{2}$/u, 'Invalid time format (HH:mm)')
                  .test(
                    'is-after-start',
                    'End time must be after start time',
                    function (value) {
                      const { start_time } = this.parent as {
                        start_time?: string;
                      };
                      if (!start_time || !value) return true;
                      return value > start_time;
                    }
                  ),
              otherwise: (schema) => schema.notRequired().nullable(),
            }
          ),
        price_amount: Yup.number()
          .typeError('Price must be a valid number')
          .positive('Price must be greater than 0')
          .nullable()
          .notRequired(),
        transaction_presentation_mode: Yup.string().nullable().notRequired(),
        access_handover_mode: Yup.string()
          .oneOf(['NONE', 'KEY_BOX', 'ACCESS_CODE'])
          .notRequired(),
        key_box_code: Yup.string().when('access_handover_mode', {
          is: (m: string) => m === 'KEY_BOX' || m === 'ACCESS_CODE',
          then: (schema) => schema.required('Key box code is required').trim(),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),
        key_box_pickup_instructions: Yup.string().when('access_handover_mode', {
          is: (m: string) => m === 'KEY_BOX' || m === 'ACCESS_CODE',
          then: (schema) =>
            schema.required('Pickup / usage instructions are required').trim(),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),
        key_box_return_instructions: Yup.string().when('access_handover_mode', {
          is: 'KEY_BOX',
          then: (schema) =>
            schema.required('Return instructions are required').trim(),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),
        ...(harborId
          ? {
              harbour_service_model: Yup.string()
                .oneOf(
                  Object.values(HarbourServiceModel),
                  'Invalid service model'
                )
                .required('Harbour service type is required'),
            }
          : {}),
        latitude: Yup.number()
          .transform((value, originalValue) => {
            if (
              originalValue === '' ||
              originalValue === null ||
              originalValue === undefined
            ) {
              return undefined;
            }
            if (typeof originalValue === 'string') {
              const normalized = originalValue
                .replace(/\s/g, '')
                .replace(',', '.');
              const parsed = Number(normalized);
              return Number.isNaN(parsed) ? NaN : parsed;
            }
            return value;
          })
          .typeError('Latitude must be a valid number')
          .min(-90, 'Latitude must be between -90 and 90')
          .max(90, 'Latitude must be between -90 and 90')
          .required('Latitude is required'),
        longitude: Yup.number()
          .transform((value, originalValue) => {
            if (
              originalValue === '' ||
              originalValue === null ||
              originalValue === undefined
            ) {
              return undefined;
            }
            if (typeof originalValue === 'string') {
              const normalized = originalValue
                .replace(/\s/g, '')
                .replace(',', '.');
              const parsed = Number(normalized);
              return Number.isNaN(parsed) ? NaN : parsed;
            }
            return value;
          })
          .typeError('Longitude must be a valid number')
          .min(-180, 'Longitude must be between -180 and 180')
          .max(180, 'Longitude must be between -180 and 180')
          .required('Longitude is required'),
      };

      const distanceSchemaShape = showDistanceFromHarbor
        ? {
            distance: Yup.number()
              .typeError('Distance must be a valid number')
              .positive('Distance must be greater than 0')
              .nullable()
              .notRequired(),
            distance_unit: Yup.string()
              .oneOf(['m', 'km'], 'Invalid distance unit')
              .nullable()
              .when('distance', {
                is: (val: unknown) =>
                  val !== undefined && val !== null && val !== '',
                then: (schema) =>
                  schema.required(
                    'Distance unit is required when distance is provided'
                  ),
                otherwise: (schema) => schema.notRequired(),
              }),
          }
        : {};

      const schema = Yup.object().shape({
        ...baseSchemaShape,
        ...(distanceSchemaShape as Record<string, Yup.AnySchema>),
      });

      await schema.validate(formDataWithDefaults, { abortEarly: false });
      setErrors({});

      // Get translations if they don't exist
      let finalTranslations = formData.translations;
      if (
        !formData.translations ||
        Object.keys(formData.translations).length === 0
      ) {
        try {
          const translateResponse = await translateService({
            description: formData.description || '',
            source_language: formData.source_language || 'en',
          }).unwrap();

          if (translateResponse.status && translateResponse.translations) {
            finalTranslations = translateResponse.translations;
            setFormData((prev) => ({
              ...prev,
              translations: finalTranslations,
            }));
          }
        } catch (error) {
          console.error('Translation failed:', error);
          enqueueSnackbar('Failed to generate translations', {
            variant: 'warning',
          });
        }
      }

      // Create FormData for file upload
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', formData.name || '');
      formDataToSubmit.append('description', formData.description || '');
      formDataToSubmit.append('category', formData.category || '');

      const selectedSubCategory = subCategories.find(
        (subCat) => subCat.value === formData.sub_category
      );
      formDataToSubmit.append(
        'sub_category',
        selectedSubCategory?.name || formData.sub_category || ''
      );

      formDataToSubmit.append('service_type', formData.service_type || '');
      formDataToSubmit.append(
        'source_language',
        formData.source_language || 'en'
      );
      formDataToSubmit.append('contact_email', formData.contact_email || '');
      formDataToSubmit.append(
        'contact_phone',
        normalizeNationalPhoneNumber(
          formData.contact_phone || '',
          formData.contact_phone_country_short_code || formData.country_short_code
        )
      );

      formDataToSubmit.append('country_code', formData.country_code || '');
      formDataToSubmit.append(
        'country_short_code',
        formData.country_short_code || ''
      );
      formDataToSubmit.append(
        'contact_phone_country_short_code',
        formData.contact_phone_country_short_code || ''
      );

      if (finalTranslations && Object.keys(finalTranslations).length > 0) {
        let mergedTranslations = { ...finalTranslations };
        const sl = formData.source_language || 'en';
        if (
          harborId &&
          (formData.access_handover_mode === 'KEY_BOX' ||
            formData.access_handover_mode === 'ACCESS_CODE')
        ) {
          mergedTranslations = {
            ...mergedTranslations,
            [sl]: {
              ...(mergedTranslations[sl] || {}),
              name: mergedTranslations[sl]?.name ?? formData.name ?? '',
              description:
                mergedTranslations[sl]?.description ??
                formData.description ??
                '',
              key_box_pickup_instructions:
                formData.key_box_pickup_instructions ??
                mergedTranslations[sl]?.key_box_pickup_instructions,
              key_box_return_instructions:
                formData.key_box_return_instructions ??
                mergedTranslations[sl]?.key_box_return_instructions,
            },
          };
        }
        formDataToSubmit.append(
          'translations',
          JSON.stringify(mergedTranslations)
        );
      }

      if (formData.service_provider_id) {
        formDataToSubmit.append(
          'service_provider_id',
          formData.service_provider_id
        );
      }

      if (formData.linked_harbor_ids && formData.linked_harbor_ids.length > 0) {
        const linkedHarborIdsJson = JSON.stringify(formData.linked_harbor_ids);
        formDataToSubmit.append('linked_harbor_ids', linkedHarborIdsJson);
      }

      if (formData.harbor_id) {
        formDataToSubmit.append('harborId', formData.harbor_id);
      }

      if (formData.latitude !== undefined && formData.latitude !== null) {
        formDataToSubmit.append('latitude', String(formData.latitude));
      }

      if (formData.longitude !== undefined && formData.longitude !== null) {
        formDataToSubmit.append('longitude', String(formData.longitude));
      }

      if (showDistanceFromHarbor) {
        if (formData.distance !== undefined && formData.distance !== null) {
          formDataToSubmit.append(
            'distance',
            String(Number(formData.distance))
          );
          formDataToSubmit.append(
            'distance_unit',
            formData.distance_unit ?? 'null'
          );
        } else {
          formDataToSubmit.append('distance', 'null');
          formDataToSubmit.append('distance_unit', 'null');
        }
      }

      if (formData.independent_booking_enabled) {
        formDataToSubmit.append('independent_booking_enabled', 'true');

        if (formData.booking_slot_type) {
          formDataToSubmit.append(
            'booking_slot_type',
            String(formData.booking_slot_type)
          );
        }
        if (formData.booking_slot_type === BookingSlotType.HOURLY) {
          formDataToSubmit.append('start_time', formData.start_time || '');
          formDataToSubmit.append('end_time', formData.end_time || '');
        }
        if (
          formData.booking_slot_type === BookingSlotType.SESSION &&
          formData.session_slots
        ) {
          formData.session_slots.forEach((slot) => {
            formDataToSubmit.append('session_slots[]', JSON.stringify(slot));
          });
        }
        if (formData.send_request_booking_to_admin !== undefined) {
          formDataToSubmit.append(
            'send_request_booking_to_admin',
            String(Boolean(formData.send_request_booking_to_admin))
          );
        }

        if (
          formData.price_amount !== undefined &&
          formData.price_amount !== null
        ) {
          formDataToSubmit.append(
            'price_amount',
            String(formData.price_amount)
          );
        }
        if (formData.price_currency) {
          formDataToSubmit.append('price_currency', formData.price_currency);
        }

        if (formData.quantity !== undefined && formData.quantity !== null) {
          formDataToSubmit.append('quantity', String(formData.quantity));
        }
      } else {
        formDataToSubmit.append('independent_booking_enabled', 'false');
      }

      if (formData.media && Array.isArray(formData.media)) {
        formData.media.forEach((file, index) => {
          if (file instanceof File) {
            formDataToSubmit.append(`media[${index}]`, file);
          }
        });

        const existingMedia = formData.media.filter(
          (item) => typeof item === 'string'
        );
        const initialMedia =
          (serviceResponse?.data as IServiceData)?.images || [];
        const mediaToDelete = initialMedia.filter(
          (url) => !existingMedia.includes(url)
        );

        if (mediaToDelete.length > 0) {
          formDataToSubmit.append(
            'mediaToDelete',
            JSON.stringify(mediaToDelete)
          );
        }
      }

      if (formData.media_captions && formData.media_captions.length > 0) {
        formDataToSubmit.append(
          'image_captions',
          JSON.stringify(formData.media_captions)
        );
      }

      if (harborId) {
        if (formData.harbour_service_model) {
          formDataToSubmit.append(
            'harbour_service_model',
            formData.harbour_service_model
          );
        }
        if (formData.transaction_presentation_mode) {
          formDataToSubmit.append(
            'transaction_presentation_mode',
            formData.transaction_presentation_mode
          );
        }
        if (formData.access_handover_mode) {
          formDataToSubmit.append(
            'access_handover_mode',
            formData.access_handover_mode
          );
        }
        if (
          formData.access_handover_mode === 'KEY_BOX' ||
          formData.access_handover_mode === 'ACCESS_CODE'
        ) {
          formDataToSubmit.append('key_box_code', formData.key_box_code || '');
          formDataToSubmit.append(
            'key_box_pickup_instructions',
            formData.key_box_pickup_instructions || ''
          );
          formDataToSubmit.append(
            'key_box_return_instructions',
            formData.key_box_return_instructions || ''
          );
        }
      }

      // Determine which API to use based on harborId presence
      if (mode === 'create') {
        if (serviceRequestId) {
          // Called from service request approval - approve request and create service
          await approveAndCreateService({
            id: serviceRequestId,
            data: formDataToSubmit,
            harborId: harborId ?? undefined,
          }).unwrap();
        } else if (harborId) {
          // Called from harbor details - use harbor service API
          await createHarborService({
            harborId,
            data: formDataToSubmit,
          }).unwrap();
        } else {
          // Called from service provider - use regular service API
          await createService(formDataToSubmit).unwrap();
        }
        enqueueSnackbar('Service created successfully', { variant: 'success' });
        navigate(-1);
      } else if (mode === 'edit' && serviceId) {
        if (harborId) {
          // Called from harbor details - use harbor service API
          await updateHarborService({
            harborId,
            serviceId,
            data: formDataToSubmit,
          }).unwrap();
        } else {
          // Called from service provider - use regular service API
          await updateService({
            serviceId,
            data: formDataToSubmit,
          }).unwrap();
        }
        enqueueSnackbar('Service updated successfully', { variant: 'success' });
        navigate(-1);
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
        enqueueSnackbar('Please fix the form errors before submitting', {
          variant: 'error',
        });
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'An error occurred';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isServiceLoading) {
    return (
      <Container>
        <div className="text-center py-8">Loading service...</div>
      </Container>
    );
  }

  if (isServiceError && (mode === 'edit' || mode === 'view')) {
    return (
      <Container>
        <div className="text-center py-8 text-red-500">
          Failed to load service. Please try again.
        </div>
      </Container>
    );
  }

  const showBookingFields = Boolean(formData.independent_booking_enabled);

  return (
    <Container>
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-gray-800">
            {mode === 'create'
              ? 'Create Service'
              : mode === 'edit'
                ? 'Edit Service'
                : 'View Service'}
          </h1>
          {mode !== 'view' && (
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={
                mode === 'create'
                  ? 'Cancel service creation'
                  : 'Cancel service editing'
              }
            >
              <KeenIcon type="filled" icon="cross" className="text-gray-500" />
              Cancel
            </button>
          )}
          {mode === 'view' && (
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Back to previous page"
            >
              <KeenIcon type="filled" icon="cross" className="text-gray-500" />
              Back
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
            {/* Name */}
            <div className="col-span-1">
              <label
                htmlFor="name-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Service Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name-input"
                type="text"
                name="name"
                data-testid="name-input"
                autoComplete="off"
                ref={nameInputRef}
                value={formData.name || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={
                  mode === 'view' || isSubmitting || isLoadingServiceCategories
                }
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 text-gray-900 disabled:text-gray-700 disabled:opacity-100',
                  errors.name && 'border-red-500'
                )}
                placeholder="Enter service name"
                required
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Service Provider - Only for admin */}
            {userData?.roles?.includes(UserRoles.ADMIN) &&
              !hideServiceProvider && (
                <div className="col-span-1">
                  <label
                    htmlFor="service_provider_id"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Service Provider <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="service_provider_id"
                    required
                    name="service_provider_id"
                    value={formData.service_provider_id || ''}
                    onChange={handleInputChange}
                    disabled={
                      mode === 'view' ||
                      isSubmitting ||
                      isLoadingProviders ||
                      !!serviceProviderId
                    }
                    className={clsx(
                      'select select-bordered w-full h-11 rounded-lg border-gray-300',
                      errors.service_provider_id && 'border-red-500'
                    )}
                  >
                    <option value="">
                      {isLoadingProviders
                        ? 'Loading...'
                        : 'Select a service provider'}
                    </option>
                    {serviceProvidersData?.data?.map((sp) => (
                      <option key={sp.id} value={sp.id}>
                        {sp.display_name}
                      </option>
                    ))}
                  </select>
                  {errors.service_provider_id && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.service_provider_id}
                    </p>
                  )}
                </div>
              )}

            {/* Category */}
            <div className="col-span-1">
              <label
                htmlFor="category-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category-input"
                name="category"
                value={formData.category || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.category && 'border-red-500'
                )}
                required
              >
                <option value="">
                  {isLoadingServiceCategories
                    ? 'Loading categories...'
                    : 'Select a category'}
                </option>
                {serviceCategoryOptions.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="text-red-500 text-xs mt-1">{errors.category}</p>
              )}
            </div>

            {/* Sub Category */}
            <div className="col-span-1">
              <label
                htmlFor="sub_category-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Sub Category <span className="text-red-500">*</span>
              </label>
              <select
                id="sub_category-input"
                name="sub_category"
                value={formData.sub_category || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={
                  mode === 'view' ||
                  isSubmitting ||
                  !formData.category ||
                  isLoadingSubCategories
                }
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.sub_category && 'border-red-500'
                )}
                required
              >
                <option value="">
                  {isLoadingSubCategories
                    ? 'Loading...'
                    : !formData.category
                      ? 'Select a category first'
                      : 'Select a sub-category'}
                </option>
                {subCategories.map((subCat) => (
                  <option key={subCat.value} value={subCat.value}>
                    {formatLabel(subCat.label)}
                  </option>
                ))}
              </select>
              {errors.sub_category && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.sub_category}
                </p>
              )}
            </div>

            {/* Service Type */}
            <div className="col-span-1">
              <label
                htmlFor="service_type-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Service Type <span className="text-red-500">*</span>
              </label>
              <select
                id="service_type-input"
                name="service_type"
                value={formData.service_type || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.service_type && 'border-red-500'
                )}
                required
              >
                <option value="">Select a service type</option>
                <option value={ServiceType.ONSITE}>Onsite</option>
                <option value={ServiceType.OFFSITE}>Offsite</option>
              </select>
              {errors.service_type && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.service_type}
                </p>
              )}
            </div>
            {harborId && (
              <div className="col-span-1 md:col-span-2 space-y-4">
                {/* Service model selector */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Harbour service type{' '}
                    {mode !== 'view' && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    name="harbour_service_model"
                    aria-label="Harbour service type"
                    value={formData.harbour_service_model ?? ''}
                    onChange={handleInputChange}
                    disabled={mode === 'view' || isSubmitting}
                    className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                  >
                    <option value="">Select service type</option>
                    <option value={HarbourServiceModel.LISTING}>
                      Service Listing (informational, no booking)
                    </option>
                    <option value={HarbourServiceModel.BOOKABLE}>
                      Bookable Service (can be reserved/booked)
                    </option>
                    <option value={HarbourServiceModel.REQUEST}>
                      Request-based Service (user submits request, harbour
                      confirms)
                    </option>
                  </select>
                  {errors.harbour_service_model && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.harbour_service_model}
                    </p>
                  )}
                  {formData.harbour_service_model ===
                    HarbourServiceModel.LISTING && (
                    <p className="text-xs text-gray-500 mt-1">
                      An informational listing. No booking, payment, or
                      access-code logic applies.
                    </p>
                  )}
                  {formData.harbour_service_model ===
                    HarbourServiceModel.BOOKABLE && (
                    <p className="text-xs text-gray-500 mt-1">
                      A harbour-managed service that boaters can actively book
                      or reserve through the platform.
                    </p>
                  )}
                  {formData.harbour_service_model ===
                    HarbourServiceModel.REQUEST && (
                    <p className="text-xs text-gray-500 mt-1">
                      A request-based service where the user submits a request
                      and the harbour handles or confirms it manually. Booking
                      and access fields are optional.
                    </p>
                  )}
                </div>

                {/* Booking and access configuration – for bookable and request-based services */}
                {(formData.harbour_service_model ===
                  HarbourServiceModel.BOOKABLE ||
                  formData.harbour_service_model ===
                    HarbourServiceModel.REQUEST) && (
                  <>
                    <h2 className="text-sm font-semibold text-gray-800">
                      Booking and access configuration
                      {formData.harbour_service_model ===
                        HarbourServiceModel.REQUEST && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          (optional for request-based services)
                        </span>
                      )}
                    </h2>
                    <p className="text-xs text-gray-500">
                      Leave presentation empty to use the harbour default (Book
                      vs Pay). Key box values stay private until after
                      successful payment. The required pickup and return texts
                      follow the <strong>Source language</strong> you select
                      elsewhere on this form; optional fields below add other
                      languages for boaters.
                    </p>
                    {formData.harbour_service_model ===
                      HarbourServiceModel.REQUEST && (
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="independent_booking_enabled"
                          checked={Boolean(
                            formData.independent_booking_enabled
                          )}
                          onChange={handleInputChange}
                          disabled={mode === 'view' || isSubmitting}
                          className="checkbox cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">
                          Include booking / payment configuration for this
                          request-based service
                        </span>
                      </label>
                    )}
                    {(formData.harbour_service_model !==
                      HarbourServiceModel.REQUEST ||
                      showBookingFields) && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Book / Pay presentation
                            </label>
                            <select
                              name="transaction_presentation_mode"
                              aria-label="Book / Pay presentation"
                              value={
                                formData.transaction_presentation_mode ?? ''
                              }
                              onChange={handleInputChange}
                              disabled={mode === 'view' || isSubmitting}
                              className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                            >
                              <option value="">Use harbour default</option>
                              <option value="RESERVATION">Book</option>
                              <option value="PAYMENT_ONLY">Pay</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {intl.formatMessage({
                                id: 'SERVICE.ACCESS_HANDOVER.LABEL',
                                defaultMessage: 'Access handover',
                              })}
                            </label>
                            <select
                              name="access_handover_mode"
                              aria-label="Access handover"
                              value={formData.access_handover_mode ?? 'NONE'}
                              onChange={handleInputChange}
                              disabled={mode === 'view' || isSubmitting}
                              className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                            >
                              <option value="NONE">
                                {intl.formatMessage({
                                  id: 'SERVICE.ACCESS_HANDOVER.NONE',
                                  defaultMessage: 'None',
                                })}
                              </option>
                              <option value="KEY_BOX">
                                {intl.formatMessage({
                                  id: 'SERVICE.ACCESS_HANDOVER.KEY_BOX',
                                  defaultMessage: 'Key box',
                                })}
                              </option>
                              <option value="ACCESS_CODE">
                                {intl.formatMessage({
                                  id: 'SERVICE.ACCESS_HANDOVER.ACCESS_CODE',
                                  defaultMessage: 'Access code (receipt)',
                                })}
                              </option>
                            </select>
                            {errors.access_handover_mode && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.access_handover_mode}
                              </p>
                            )}
                          </div>
                        </div>
                        {(formData.access_handover_mode === 'KEY_BOX' ||
                          formData.access_handover_mode === 'ACCESS_CODE') && (
                          <div className="grid grid-cols-1 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Key box code{' '}
                                <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                name="key_box_code"
                                aria-label="Key box code"
                                value={formData.key_box_code ?? ''}
                                onChange={handleInputChange}
                                disabled={mode === 'view' || isSubmitting}
                                className="textarea textarea-bordered w-full min-h-[72px]"
                              />
                              {errors.key_box_code && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.key_box_code}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Pickup / usage instructions{' '}
                                <span className="text-red-500">*</span>
                                <span className="block text-xs font-normal text-gray-500 mt-0.5">
                                  Language:{' '}
                                  {SOURCE_LANGUAGE_LABELS[
                                    formData.source_language || 'en'
                                  ] || formData.source_language}
                                </span>
                              </label>
                              <textarea
                                name="key_box_pickup_instructions"
                                aria-label="Pickup / usage instructions"
                                value={
                                  formData.key_box_pickup_instructions ?? ''
                                }
                                onChange={handleInputChange}
                                disabled={mode === 'view' || isSubmitting}
                                className="textarea textarea-bordered w-full min-h-[88px]"
                              />
                              {errors.key_box_pickup_instructions && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.key_box_pickup_instructions}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Return instructions{' '}
                                {formData.access_handover_mode === 'KEY_BOX' ? (
                                  <span className="text-red-500">*</span>
                                ) : (
                                  <span className="text-gray-400 text-xs font-normal">
                                    (optional)
                                  </span>
                                )}
                                <span className="block text-xs font-normal text-gray-500 mt-0.5">
                                  Language:{' '}
                                  {SOURCE_LANGUAGE_LABELS[
                                    formData.source_language || 'en'
                                  ] || formData.source_language}
                                </span>
                              </label>
                              <textarea
                                name="key_box_return_instructions"
                                aria-label="Return instructions"
                                value={
                                  formData.key_box_return_instructions ?? ''
                                }
                                onChange={handleInputChange}
                                disabled={mode === 'view' || isSubmitting}
                                className="textarea textarea-bordered w-full min-h-[88px]"
                              />
                              {errors.key_box_return_instructions && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.key_box_return_instructions}
                                </p>
                              )}
                            </div>
                            <div className="border-t border-gray-200 pt-4 mt-2">
                              <p className="text-sm font-medium text-gray-700 mb-1">
                                Instructions by language (optional)
                              </p>
                              <p className="text-xs text-gray-500 mb-3">
                                The required fields above are saved for the
                                source language. Add Finnish, Swedish, or German
                                here so post-payment access text matches the
                                boater&apos;s language.
                              </p>
                              <div className="space-y-4">
                                {I18N_LANGUAGES.filter((l) =>
                                  KEY_BOX_CONTENT_LANGS.includes(
                                    l.code as (typeof KEY_BOX_CONTENT_LANGS)[number]
                                  )
                                ).map((lang) => (
                                  <div
                                    key={lang.code}
                                    className="rounded-lg border border-gray-200 p-3 bg-gray-100"
                                  >
                                    <p className="text-xs font-semibold text-gray-600 mb-2">
                                      {lang.label}
                                    </p>
                                    <div className="grid gap-2">
                                      <textarea
                                        placeholder="Pickup / usage (optional)"
                                        value={
                                          formData.translations?.[lang.code]
                                            ?.key_box_pickup_instructions ?? ''
                                        }
                                        onChange={(e) =>
                                          handleKeyBoxLangFieldChange(
                                            lang.code,
                                            'key_box_pickup_instructions',
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          mode === 'view' || isSubmitting
                                        }
                                        className="textarea textarea-bordered w-full min-h-[64px] text-sm"
                                      />
                                      <textarea
                                        placeholder="Return (optional)"
                                        value={
                                          formData.translations?.[lang.code]
                                            ?.key_box_return_instructions ?? ''
                                        }
                                        onChange={(e) =>
                                          handleKeyBoxLangFieldChange(
                                            lang.code,
                                            'key_box_return_instructions',
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          mode === 'view' || isSubmitting
                                        }
                                        className="textarea textarea-bordered w-full min-h-[64px] text-sm"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {/* Independent Booking Configuration – only shown for non-harbour services */}
            {!harborId && (
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
                  <div className="flex-1">
                    <label
                      htmlFor="independent_booking_enabled"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Enable Independent Booking
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Allow boaters to discover and book this service without
                      selecting a marina first. Slot picker and My Bookings will
                      follow existing patterns.
                    </p>
                  </div>
                  <div className="flex items-center mt-1">
                    <input
                      id="independent_booking_enabled"
                      name="independent_booking_enabled"
                      type="checkbox"
                      autoComplete="off"
                      checked={Boolean(formData.independent_booking_enabled)}
                      onChange={handleInputChange}
                      disabled={mode === 'view' || isSubmitting}
                      aria-describedby={
                        errors.independent_booking_enabled
                          ? 'independent_booking_enabled-error'
                          : undefined
                      }
                      className="checkbox cursor-pointer"
                    />
                  </div>
                </div>
                {errors.independent_booking_enabled && (
                  <p
                    id="independent_booking_enabled-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.independent_booking_enabled}
                  </p>
                )}
              </div>
            )}

            {showBookingFields && (
              <>
                {showBookingFields && (
                  <div className="col-span-1">
                    <label
                      htmlFor="booking_slot_type"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Slot Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="booking_slot_type"
                      name="booking_slot_type"
                      value={(formData.booking_slot_type as string) || ''}
                      onChange={handleInputChange}
                      onBlur={(e) =>
                        handleInputBlur(e.target.name, e.target.value)
                      }
                      disabled={mode === 'view' || isSubmitting}
                      className={clsx(
                        'select select-bordered w-full h-11 rounded-lg border-gray-300',
                        errors.booking_slot_type && 'border-red-500'
                      )}
                      aria-invalid={!!errors.booking_slot_type}
                      aria-describedby={
                        errors.booking_slot_type
                          ? 'booking_slot_type-error'
                          : undefined
                      }
                      required
                    >
                      <option value="">Select slot type</option>
                      <option value={BookingSlotType.HOURLY}>Hourly</option>
                      <option value={BookingSlotType.DAILY}>Daily</option>
                      <option value={BookingSlotType.SESSION}>Session</option>
                    </select>
                    {errors.booking_slot_type && (
                      <p
                        id="booking_slot_type-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.booking_slot_type}
                      </p>
                    )}
                  </div>
                )}
                {showBookingFields &&
                  formData.booking_slot_type === BookingSlotType.HOURLY && (
                    <>
                      <div className="col-span-1">
                        <label
                          htmlFor="start_time"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Start Time
                        </label>
                        <input
                          id="start_time"
                          type="time"
                          name="start_time"
                          autoComplete="off"
                          value={formData.start_time || ''}
                          onChange={handleInputChange}
                          onBlur={(e) =>
                            handleInputBlur(e.target.name, e.target.value)
                          }
                          disabled={mode === 'view' || isSubmitting}
                          step="3600"
                          className={clsx(
                            baseInputClassName,
                            errors.start_time && 'border-red-500'
                          )}
                          aria-invalid={!!errors.start_time}
                          aria-describedby={
                            errors.start_time ? 'start_time-error' : undefined
                          }
                          placeholder="Select start time"
                          title="Select hour only (minutes will be set to :00)"
                        />
                        {errors.start_time && (
                          <p
                            id="start_time-error"
                            className="text-red-500 text-xs mt-1"
                            role="alert"
                          >
                            {errors.start_time}
                          </p>
                        )}
                      </div>
                      <div className="col-span-1">
                        <label
                          htmlFor="end_time"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          End Time
                        </label>
                        <input
                          id="end_time"
                          type="time"
                          name="end_time"
                          value={formData.end_time || ''}
                          onChange={handleInputChange}
                          onBlur={(e) =>
                            handleInputBlur(e.target.name, e.target.value)
                          }
                          disabled={mode === 'view' || isSubmitting}
                          step="3600"
                          autoComplete="off"
                          className={clsx(
                            baseInputClassName,
                            errors.end_time && 'border-red-500'
                          )}
                          aria-invalid={!!errors.end_time}
                          aria-describedby={
                            errors.end_time ? 'end_time-error' : undefined
                          }
                          placeholder="Select end time"
                          title="Select hour only (minutes will be set to :00)"
                        />
                        {errors.end_time && (
                          <p
                            id="end_time-error"
                            className="text-red-500 text-xs mt-1"
                            role="alert"
                          >
                            {errors.end_time}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                {showBookingFields &&
                  formData.booking_slot_type === BookingSlotType.SESSION && (
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Session Slots <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-3">
                        {formData.session_slots?.map((slot, index) => (
                          <div key={index} className="flex gap-4 items-start">
                            <div className="flex-1">
                              <input
                                type="time"
                                value={slot.start_time}
                                onChange={(e) =>
                                  handleSessionSlotChange(
                                    index,
                                    'start_time',
                                    e.target.value
                                  )
                                }
                                className={baseInputClassName}
                                placeholder="Start Time"
                                autoComplete="off"
                                required
                              />
                            </div>
                            <div className="flex-1">
                              <input
                                type="time"
                                value={slot.end_time}
                                onChange={(e) =>
                                  handleSessionSlotChange(
                                    index,
                                    'end_time',
                                    e.target.value
                                  )
                                }
                                className={baseInputClassName}
                                placeholder="End Time"
                                required
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveSessionSlot(index)}
                              className="btn btn-square btn-outline btn-error"
                            >
                              <KeenIcon icon="trash" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={handleAddSessionSlot}
                          className="btn btn-sm btn-secondary"
                        >
                          + Add Slot
                        </button>
                      </div>
                      {typeof errors.session_slots === 'string' && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.session_slots}
                        </p>
                      )}
                    </div>
                  )}

                {/* Require Admin Approval - Only show when booking is enabled */}
                {showBookingFields && (
                  <div className="col-span-1">
                    <label
                      htmlFor="send_request_booking_to_admin"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Require Admin Approval
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        id="send_request_booking_to_admin"
                        name="send_request_booking_to_admin"
                        type="checkbox"
                        checked={Boolean(
                          formData.send_request_booking_to_admin
                        )}
                        onChange={handleInputChange}
                        disabled={mode === 'view' || isSubmitting}
                        className="toggle toggle-primary cursor-pointer"
                      />
                      <span className="text-sm text-gray-600">
                        If enabled, booking requests are sent to admin for
                        approval. If disabled, users can pay and book directly.
                      </span>
                    </div>
                    {errors.send_request_booking_to_admin && (
                      <p
                        id="send_request_booking_to_admin-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                      >
                        {errors.send_request_booking_to_admin}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Price - Only show when booking is enabled */}
            {showBookingFields && (
              <div className="col-span-1">
                <label
                  htmlFor="price_amount"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Price <span className="text-red-500">*</span>
                </label>
                <PriceInput
                  id="price_amount"
                  name="price_amount"
                  value={
                    formData.price_amount !== undefined &&
                    formData.price_amount !== null
                      ? formData.price_amount
                      : ''
                  }
                  onChange={(val) => {
                    setFormData((prev: Partial<IServiceData>) => ({
                      ...prev,
                      price_amount: val,
                    }));
                    validateField('price_amount', val);
                  }}
                  disabled={mode === 'view' || isSubmitting}
                  inputClassName={clsx(
                    baseInputClassName,
                    errors.price_amount && 'border-red-500'
                  )}
                  error={errors.price_amount}
                  placeholder="e.g., 25.00"
                />
                {errors.price_amount && (
                  <p
                    id="price_amount-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.price_amount}
                  </p>
                )}
              </div>
            )}

            {/* Capacity - Always show when booking is enabled */}
            {showBookingFields && (
              <div className="col-span-1">
                <label
                  htmlFor="quantity"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Capacity <span className="text-red-500">*</span>
                </label>
                <NumberInput
                  id="quantity"
                  name="quantity"
                  min={1}
                  value={formData.quantity || ''}
                  onChange={handleInputChange}
                  onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                  disabled={mode === 'view' || isSubmitting}
                  className={clsx(
                    baseInputClassName,
                    errors.quantity && 'border-red-500'
                  )}
                  aria-invalid={!!errors.quantity}
                  aria-describedby={
                    errors.quantity ? 'quantity-error' : undefined
                  }
                  placeholder="Enter capacity"
                  required
                />
                {errors.quantity && (
                  <p
                    id="quantity-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.quantity}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {formData.booking_slot_type === BookingSlotType.HOURLY && (
                    <>
                      Number of bookings accepted <strong>per hour</strong>. If
                      set to <strong>15</strong>, customers see 15 spots
                      available for each hourly slot. Set to <strong>1</strong>{' '}
                      for exclusive or single-use services where only one
                      booking is allowed per hour.
                    </>
                  )}
                  {formData.booking_slot_type === BookingSlotType.DAILY && (
                    <>
                      Number of bookings accepted <strong>per day</strong>. If
                      set to <strong>15</strong>, up to 15 customers can book
                      the same day. Set to <strong>1</strong> for services where
                      only one booking is allowed per day.
                    </>
                  )}
                  {formData.booking_slot_type === BookingSlotType.SESSION && (
                    <>
                      Number of bookings accepted <strong>per session</strong>.
                      If set to <strong>15</strong>, up to 15 customers can book
                      each session slot. Set to <strong>1</strong> for exclusive
                      sessions where only one booking is allowed at a time.
                    </>
                  )}
                  {!formData.booking_slot_type && (
                    <>
                      Number of bookings accepted per time slot. Select a slot
                      type above to see a more specific explanation.
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Description - Full width */}
            <div className="col-span-1 md:col-span-2">
              <label
                htmlFor="description-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description-input"
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'textarea textarea-bordered w-full rounded-lg border-gray-300',
                  errors.description && 'border-red-500'
                )}
                placeholder="Enter service description"
                rows={4}
                required
              />
              {errors.description && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.description}
                </p>
              )}
            </div>

            {/* Contact Email */}
            <div className="col-span-1">
              <label
                htmlFor="contact_email-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Contact Email
              </label>
              <input
                id="contact_email-input"
                type="email"
                name="contact_email"
                value={formData.contact_email || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 text-gray-900 disabled:text-gray-700 disabled:opacity-100',
                  errors.contact_email && 'border-red-500'
                )}
                placeholder="Enter contact email address"
              />
              {errors.contact_email && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.contact_email}
                </p>
              )}
            </div>

            {/* Contact Phone */}
            <div className="col-span-1">
              <label
                htmlFor="contact_phone-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Contact Phone
              </label>
              <div className="flex gap-2">
                <div
                  style={{ width: '110px', minWidth: '110px', flexShrink: 0 }}
                >
                  <CountryCodeSelect
                    selected={formData.contact_phone_country_short_code || formData.country_short_code || 'FI'}
                    setFieldValue={(dialCode, countryShortCode) => {
                      setFormData((prev) => ({
                        ...prev,
                        contact_phone_country_short_code: countryShortCode,
                      }));
                      validateField('contact_phone_country_short_code', countryShortCode);
                    }}
                    disabled={mode === 'view' || isSubmitting}
                    hasError={!!errors.contact_phone_country_short_code}
                  />
                </div>
                <div className="flex-1">
                  <input
                    id="contact_phone-input"
                    type="text"
                    name="contact_phone"
                    value={formData.contact_phone || ''}
                    onChange={handleInputChange}
                    onBlur={(e) =>
                      handleInputBlur(e.target.name, e.target.value)
                    }
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 text-gray-900 disabled:text-gray-700 disabled:opacity-100',
                      errors.contact_phone && 'border-red-500'
                    )}
                    placeholder="Enter contact phone number"
                  />
                </div>
              </div>
              {errors.contact_phone && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.contact_phone}
                </p>
              )}
            </div>

            {/* Source Language */}
            <div className="col-span-1">
              <label
                htmlFor="source_language"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Source Language <span className="text-red-500">*</span>
              </label>
              <select
                id="source_language"
                name="source_language"
                value={formData.source_language || 'en'}
                onChange={handleInputChange}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.source_language && 'border-red-500'
                )}
                disabled={mode === 'view' || isSubmitting}
              >
                <option value={ServiceLanguage.EN}>English</option>
                <option value={ServiceLanguage.FI}>Finnish</option>
                <option value={ServiceLanguage.SV}>Swedish</option>
                <option value={ServiceLanguage.DE}>German</option>
              </select>
              {errors.source_language && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.source_language}
                </p>
              )}
            </div>

            <div className="col-span-1">
              <label
                htmlFor="latitude-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Latitude <span className="text-red-500">*</span>{' '}
                <span className="text-gray-500">(decimal)</span>
              </label>
              <NumberInput
                id="latitude-input"
                name="latitude"
                allowNegative
                step="0.0001"
                min="-90"
                max="90"
                maxDecimals={100}
                value={formData.latitude || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  baseInputClassName,
                  errors.latitude && 'border-red-500'
                )}
                aria-invalid={!!errors.latitude}
                autoComplete="off"
                aria-describedby={
                  errors.latitude ? 'latitude-error' : undefined
                }
                placeholder="Enter latitude (e.g., 60.1699)"
              />
              {errors.latitude && (
                <p
                  id="latitude-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.latitude}
                </p>
              )}
            </div>

            {/* Longitude */}
            <div className="col-span-1">
              <label
                htmlFor="longitude-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Longitude <span className="text-red-500">*</span>{' '}
                <span className="text-gray-500">(decimal)</span>
              </label>
              <NumberInput
                id="longitude-input"
                name="longitude"
                allowNegative
                step="0.0001"
                min="-180"
                max="180"
                maxDecimals={100}
                value={formData.longitude || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  baseInputClassName,
                  errors.longitude && 'border-red-500'
                )}
                aria-invalid={!!errors.longitude}
                autoComplete="off"
                aria-describedby={
                  errors.longitude ? 'longitude-error' : undefined
                }
                placeholder="Enter longitude (e.g., 24.9384)"
              />
              {errors.longitude && (
                <p
                  id="longitude-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.longitude}
                </p>
              )}
            </div>

            {/* Distance from Harbor - Conditional */}
            {showDistanceFromHarbor && (
              <>
                <div className="col-span-1">
                  <label
                    htmlFor="distance-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Distance from Harbor{' '}
                    <span className="text-gray-500">(optional)</span>
                  </label>
                  <NumberInput
                    id="distance-input"
                    name="distance"
                    step={0.01}
                    min={0}
                    value={
                      formData.distance !== undefined &&
                      formData.distance !== null
                        ? Number(formData.distance)
                        : ''
                    }
                    onChange={handleInputChange}
                    onBlur={() =>
                      handleInputBlur(
                        'distance',
                        formData.distance ?? undefined
                      )
                    }
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      baseInputClassName,
                      errors.distance && 'border-red-500'
                    )}
                    aria-invalid={!!errors.distance}
                    aria-describedby={
                      errors.distance ? 'distance-error' : undefined
                    }
                    placeholder="Enter distance from harbor"
                    autoComplete="off"
                  />
                  {errors.distance && (
                    <p
                      id="distance-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.distance}
                    </p>
                  )}
                </div>

                <div className="col-span-1">
                  <label
                    htmlFor="distance_unit"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Distance Unit{' '}
                    <span className="text-gray-500">(optional)</span>
                  </label>
                  <select
                    id="distance_unit"
                    name="distance_unit"
                    value={formData.distance_unit || ''}
                    onChange={handleInputChange}
                    onBlur={(e) =>
                      handleInputBlur(e.target.name, e.target.value)
                    }
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'select select-bordered w-full h-11 rounded-lg border-gray-300',
                      errors.distance_unit && 'border-red-500'
                    )}
                    aria-invalid={!!errors.distance_unit}
                    aria-describedby={
                      errors.distance_unit ? 'distance_unit-error' : undefined
                    }
                  >
                    <option value="">Select unit</option>
                    <option value="m">Meters (m)</option>
                    <option value="km">Kilometers (km)</option>
                  </select>
                  {errors.distance_unit && (
                    <p
                      id="distance_unit-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.distance_unit}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Media Upload */}
            <div className="col-span-1 md:col-span-2">
              <MediaUpload
                field="media"
                media={formData.media}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                disabled={mode === 'view' || isSubmitting}
                allowedTypes={[
                  'image/jpeg',
                  'image/jpg',
                  'image/png',
                  'video/mp4',
                ]}
                maxFiles={10}
                previewMode={true}
                media_captions={formData.media_captions}
              />
            </div>
          </div>

          {/* Preview Message */}
          {showPreviewButton && (
            <div className="mt-6 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-4 flex items-center justify-between gap-2">
              <div className="flex items-center">
                <KeenIcon
                  type="outline"
                  icon="paper-clip"
                  className="w-5 h-5 text-blue-600 dark:text-blue-100 contrast:text-blue-100 mr-2"
                />
                <p className="text-blue-700 dark:text-blue-100 contrast:text-blue-100 text-sm">
                  Name and description are filled. You can preview translations
                  in different languages.
                </p>
              </div>
              <button
                type="button"
                onClick={handleOpenPreview}
                disabled={isTranslatingPreview}
                className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 flex items-center gap-1.5"
              >
                {isTranslatingPreview && (
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {isTranslatingPreview ? 'Preparing Preview…' : 'Show Preview'}
              </button>
            </div>
          )}

          {/* Action Buttons */}
          {mode !== 'view' && (
            <div className="mt-8 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-sm btn-ghost text-gray-600 bg-gray-200 hover:bg-gray-300 dark:bg-coal-300 dark:text-gray-800 dark:hover:bg-coal-200 rounded-lg px-4 py-2"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-sm btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? 'Saving...'
                  : mode === 'create'
                    ? 'Create'
                    : 'Save'}
              </button>
            </div>
          )}

          {/* Preview Modal */}
          <PreviewModal
            open={isPreviewModalOpen}
            onClose={() => setIsPreviewModalOpen(false)}
            formData={{
              name: formData.name,
              description: formData.description,
              translations: formData.translations
                ? Object.fromEntries(
                    Object.entries(formData.translations)
                      .filter(([key]) => !key.includes('_'))
                      .map(([lang, content]) => [
                        lang,
                        {
                          name: content.name || '',
                          description: content.description || '',
                        },
                      ])
                  )
                : {},
              source_language: formData.source_language,
            }}
            initialTranslations={formData.translations}
            source_language={formData.source_language || 'en'}
            onTranslationsUpdate={handleTranslationsUpdate}
            onSourceFormDataChange={handleSourceFormDataChange}
          />
        </div>
      </div>
    </Container>
  );
};

export default ServiceFormPage;
